import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Checkbox, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UndoIcon from '@mui/icons-material/Undo';
import {
    GlColumnDef, GlSheetRowVO, buildColumnKey, mergeGlRow6Display, resolvePriorYearAmount,
} from '@/services/accountService/glInvoiceService';
import { resolveGlCurrentPeriodDisplay } from '@/utils/glToFsMapper';
import {
    isExRateColumn,
    buildBankGroups,
    sumByKeys,
    BankGroup,
    splitBankGroupsByAccountType,
    filterNonEmptyBankColumns,
    bankColumnShortLabel,
    buildExcludedBankSummaryKeys,
} from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { sameSignOrNeutral } from '@/components/account/gl-invoice/mode/glTransferAlloc';
import TinyRowSearch from '@/pages/components/account/gl-invoice/mode/TinyRowSearch';
import { tinyRowMatchesQuery } from '@/pages/components/account/gl-invoice/mode/matchTinyRow';

export interface GlBankTransferPick {
    row: GlSheetRowVO;
    amount: number;
    glCode: string | null;
}

interface GlBankCashDetailProps {
    columnDefs: GlColumnDef[];
    rows: GlSheetRowVO[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    onRowClick?: (row: GlSheetRowVO, amount: number, glCode?: string | null) => void;
    transferSelectable?: boolean;
    onTransferSelected?: (picks: GlBankTransferPick[]) => void;
    onReturnSelected?: (picks: GlBankTransferPick[]) => void;
    searchOpen?: boolean;
    searchQuery?: string;
    onSearchToggle?: () => void;
    onSearchQueryChange?: (value: string) => void;
}

/** 同一发票上除银行现金外的非零科目 */
function buildBankRelatedEntries(
    columnValues: Record<string, number> | undefined,
    columnDefs: GlColumnDef[],
): { amount: number; label: string }[] {
    const excludedBankKeys = buildExcludedBankSummaryKeys(columnDefs);
    const entries: { amount: number; label: string }[] = [];
    for (const [k, raw] of Object.entries(columnValues || {})) {
        const v = Number(raw) || 0;
        if (v === 0) continue;
        if (k === 'BS-CA-CASH' || k.startsWith('BS-CA-CASH|')) continue;
        if (excludedBankKeys.has(k)) continue;
        const def = columnDefs.find((d) => buildColumnKey(d) === k);
        if (def && isExRateColumn(def)) continue;
        const label = def?.nameEn || def?.fsNameEn || def?.fsCode
            || (k.includes('|') ? k.slice(0, k.indexOf('|')) : k);
        entries.push({ amount: v, label });
    }
    entries.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    return entries;
}

function formatNum(v: number | null | undefined): string {
    if (v == null || v === 0) return '—';
    if (v < 0) {
        return `(${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function groupKeys(group: BankGroup, excludedKeys: Set<string>): string[] {
    return group.columns
        .filter((c) => !isExRateColumn(c))
        .map((c) => buildColumnKey(c))
        .filter((k) => !excludedKeys.has(k));
}

/** 取該組期末余额（当期变动 + 上年），与 FS Summary / Original 模式同源 */
function groupDisplayTotal(
    group: BankGroup,
    summaryCurrentPeriod: Record<string, number>,
    summaryPriorYear: Record<string, number>,
    excludedKeys: Set<string>,
): number {
    const keys = groupKeys(group, excludedKeys);
    const movement = sumByKeys(summaryCurrentPeriod, keys);
    const prior = keys.reduce((s, k) => s + resolvePriorYearAmount(k, summaryPriorYear), 0);
    return mergeGlRow6Display(movement, prior, 'BS-CA-CASH');
}

function groupPriorTotal(
    group: BankGroup,
    summaryPriorYear: Record<string, number>,
    excludedKeys: Set<string>,
): number {
    return groupKeys(group, excludedKeys)
        .reduce((s, k) => s + resolvePriorYearAmount(k, summaryPriorYear), 0);
}

/** 過濾出該組任一列有值的行 */
function filterGroupRows(group: BankGroup, rows: GlSheetRowVO[]): GlSheetRowVO[] {
    return rows.filter((row) => {
        for (const col of group.columns) {
            const val = row.columnValues?.[buildColumnKey(col)];
            if (val != null && val !== 0) return true;
        }
        return false;
    });
}

/** 取该行在组内绝对值最大的银行列作为转移源 */
export function resolveBankRowTransfer(
    row: GlSheetRowVO,
    columns: GlColumnDef[],
): { amount: number; glCode: string | null } {
    let best: { abs: number; amount: number; glCode: string | null } | null = null;
    for (const col of columns) {
        if (isExRateColumn(col)) continue;
        const val = Number(row.columnValues?.[buildColumnKey(col)] ?? 0);
        if (val === 0) continue;
        const abs = Math.abs(val);
        if (!best || abs > best.abs) {
            best = { abs, amount: val, glCode: col.glCode || null };
        }
    }
    return best ?? { amount: 0, glCode: null };
}

export function GlBankCashDetail(props: GlBankCashDetailProps) {
    const {
        columnDefs,
        rows,
        summaryCurrentPeriod,
        summaryPriorYear,
        onRowClick,
        transferSelectable,
        onTransferSelected,
        onReturnSelected,
        searchOpen = false,
        searchQuery = '',
        onSearchToggle,
        onSearchQueryChange,
    } = props;

    const excludedBankKeys = useMemo(
        () => buildExcludedBankSummaryKeys(columnDefs),
        [columnDefs],
    );

    const bankGroups = useMemo(() => {
        const groups = buildBankGroups(columnDefs);
        return splitBankGroupsByAccountType(groups);
    }, [columnDefs]);

    const q = searchQuery.trim();

    // 有当期明细，或仅有上年余额的组都要展示；搜索时只保留命中行，合计仍用科目汇总
    const nonEmptyGroups = useMemo(() => {
        return bankGroups
            .map((group) => {
                const groupRows = filterGroupRows(group, rows);
                const filteredRows = q
                    ? groupRows.filter((row) => tinyRowMatchesQuery(row, q))
                    : groupRows;
                return {
                    group,
                    filteredRows,
                    prior: groupPriorTotal(group, summaryPriorYear, excludedBankKeys),
                    movement: sumByKeys(summaryCurrentPeriod, groupKeys(group, excludedBankKeys)),
                };
            })
            .filter(({ filteredRows, prior, movement }) =>
                q
                    ? filteredRows.length > 0
                    : filteredRows.length > 0 || prior !== 0 || movement !== 0);
    }, [bankGroups, rows, summaryCurrentPeriod, summaryPriorYear, excludedBankKeys, q]);

    const matchCount = useMemo(() => {
        if (!q) return null;
        return nonEmptyGroups.reduce((sum, g) => sum + g.filteredRows.length, 0);
    }, [nonEmptyGroups, q]);

    const bankTotal = useMemo(() => {
        return resolveGlCurrentPeriodDisplay(
            'BS-CA-CASH', summaryCurrentPeriod, summaryPriorYear, columnDefs,
        );
    }, [columnDefs, summaryCurrentPeriod, summaryPriorYear]);

    if (bankGroups.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                No bank / cash data
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, gap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Bank Balance and Cash
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            銀行結餘及現金 (BS-CA-CASH) · Total = Current + Prior Year
                        </Typography>
                    </Box>
                    {onSearchToggle && onSearchQueryChange && (
                        <TinyRowSearch
                            open={searchOpen}
                            query={searchQuery}
                            matchCount={matchCount}
                            onToggle={onSearchToggle}
                            onQueryChange={onSearchQueryChange}
                        />
                    )}
                </Stack>
                <Typography variant="subtitle1" fontWeight={700}>
                    Total: {formatNum(bankTotal)}
                </Typography>
            </Box>

            {nonEmptyGroups.length === 0 ? (
                <Typography sx={{ py: 2, color: 'text.secondary', textAlign: 'center' }}>
                    {q ? 'No matching rows' : 'No bank / cash data'}
                </Typography>
            ) : nonEmptyGroups.map(({ group, filteredRows }) => (
                <BankGroupSection
                    key={group.groupLabel}
                    group={group}
                    filteredRows={filteredRows}
                    summaryCurrentPeriod={summaryCurrentPeriod}
                    summaryPriorYear={summaryPriorYear}
                    excludedBankKeys={excludedBankKeys}
                    columnDefs={columnDefs}
                    onRowClick={onRowClick}
                    transferSelectable={transferSelectable}
                    onTransferSelected={onTransferSelected}
                    onReturnSelected={onReturnSelected}
                />
            ))}
        </Box>
    );
}

function BankGroupSection({
    group,
    filteredRows,
    summaryCurrentPeriod,
    summaryPriorYear,
    excludedBankKeys,
    columnDefs,
    onRowClick,
    transferSelectable,
    onTransferSelected,
    onReturnSelected,
}: {
    group: BankGroup;
    filteredRows: GlSheetRowVO[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    excludedBankKeys: Set<string>;
    columnDefs: GlColumnDef[];
    onRowClick?: (row: GlSheetRowVO, amount: number, glCode?: string | null) => void;
    transferSelectable?: boolean;
    onTransferSelected?: (picks: GlBankTransferPick[]) => void;
    onReturnSelected?: (picks: GlBankTransferPick[]) => void;
}) {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    const [selectError, setSelectError] = useState<string | null>(null);

    useEffect(() => {
        setSelectedIds(new Set());
        setSelectError(null);
    }, [group.groupLabel, filteredRows[0]?.invoiceId]);

    const visibleColumns = useMemo(() => {
        const cols = filterNonEmptyBankColumns(group, filteredRows, summaryCurrentPeriod);
        // 也保留仅有上年余额的列
        const priorOnly = group.columns.filter((col) => {
            if (isExRateColumn(col)) return false;
            const key = buildColumnKey(col);
            if (excludedBankKeys.has(key)) return false;
            if (cols.some((c) => buildColumnKey(c) === key)) return false;
            return resolvePriorYearAmount(key, summaryPriorYear) !== 0;
        });
        return [...cols, ...priorOnly];
    }, [group, filteredRows, summaryCurrentPeriod, summaryPriorYear, excludedBankKeys]);

    const displayGroup: BankGroup = {
        ...group,
        columns: visibleColumns.length > 0 ? visibleColumns : group.columns,
        columnKeys: (visibleColumns.length > 0 ? visibleColumns : group.columns).map((c) => buildColumnKey(c)),
    };
    const total = groupDisplayTotal(displayGroup, summaryCurrentPeriod, summaryPriorYear, excludedBankKeys);
    const prior = groupPriorTotal(displayGroup, summaryPriorYear, excludedBankKeys);
    const hasPriorRow = prior !== 0
        || displayGroup.columns.some((col) => {
            const key = buildColumnKey(col);
            return !excludedBankKeys.has(key) && resolvePriorYearAmount(key, summaryPriorYear) !== 0;
        });

    const selectableRows = useMemo(
        () => filteredRows.filter((r) => !!r.invoiceId && !String(r.invoiceId).startsWith('__prior_bf__')),
        [filteredRows],
    );

    const resolvePick = useCallback((row: GlSheetRowVO): GlBankTransferPick => {
        const resolved = resolveBankRowTransfer(row, displayGroup.columns);
        return { row, amount: resolved.amount, glCode: resolved.glCode };
    }, [displayGroup.columns]);

    const allChecked = selectableRows.length > 0
        && selectableRows.every((r) => selectedIds.has(r.invoiceId));
    const someChecked = selectableRows.some((r) => selectedIds.has(r.invoiceId));

    const toggleRow = useCallback((row: GlSheetRowVO, checked: boolean) => {
        if (!row.invoiceId) return;
        const pick = resolvePick(row);
        setSelectError(null);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (!checked) {
                next.delete(row.invoiceId);
                return next;
            }
            // 同符号 + 同 glCode
            let refSign = 0;
            let refGl: string | null | undefined = undefined;
            for (const id of prev) {
                const other = filteredRows.find((r) => r.invoiceId === id);
                if (!other) continue;
                const op = resolvePick(other);
                if (op.amount !== 0) {
                    refSign = Math.sign(op.amount);
                    refGl = op.glCode;
                    break;
                }
            }
            if (refSign !== 0 && pick.amount !== 0 && !sameSignOrNeutral(refSign, pick.amount)) {
                setSelectError('Selected rows must have the same amount sign');
                return prev;
            }
            if (refGl !== undefined && (refGl || null) !== (pick.glCode || null)) {
                setSelectError('Selected rows must be from the same bank account column');
                return prev;
            }
            next.add(row.invoiceId);
            return next;
        });
    }, [filteredRows, resolvePick]);

    const toggleAll = useCallback((checked: boolean) => {
        setSelectError(null);
        if (!checked) {
            setSelectedIds(new Set());
            return;
        }
        let refSign = 0;
        let refGl: string | null | undefined = undefined;
        for (const row of selectableRows) {
            const pick = resolvePick(row);
            if (pick.amount !== 0) {
                refSign = Math.sign(pick.amount);
                refGl = pick.glCode;
                break;
            }
        }
        const next = new Set<string>();
        let skipped = false;
        for (const row of selectableRows) {
            const pick = resolvePick(row);
            if (refSign !== 0 && pick.amount !== 0 && !sameSignOrNeutral(refSign, pick.amount)) {
                skipped = true;
                continue;
            }
            if (refGl !== undefined && (refGl || null) !== (pick.glCode || null)) {
                skipped = true;
                continue;
            }
            next.add(row.invoiceId);
        }
        setSelectedIds(next);
        if (skipped) setSelectError('Only same-sign / same-account rows were selected');
    }, [resolvePick, selectableRows]);

    const handleTransferSelected = useCallback(() => {
        if (!onTransferSelected || selectedIds.size === 0) return;
        const picks: GlBankTransferPick[] = [];
        for (const row of filteredRows) {
            if (!selectedIds.has(row.invoiceId)) continue;
            picks.push(resolvePick(row));
        }
        if (picks.length === 0) return;
        onTransferSelected(picks);
    }, [filteredRows, onTransferSelected, resolvePick, selectedIds]);

    const handleReturnSelected = useCallback(() => {
        if (!onReturnSelected || selectedIds.size === 0) return;
        const picks: GlBankTransferPick[] = [];
        for (const row of filteredRows) {
            if (!selectedIds.has(row.invoiceId)) continue;
            picks.push(resolvePick(row));
        }
        if (picks.length === 0) return;
        onReturnSelected(picks);
    }, [filteredRows, onReturnSelected, resolvePick, selectedIds]);

    const cellSx: React.CSSProperties = {
        padding: '3px 8px',
        fontSize: 12,
        whiteSpace: 'nowrap',
        borderRight: '1px solid #eee',
        borderBottom: '1px solid #eee',
    };
    const priorRowSx: React.CSSProperties = {
        ...cellSx,
        background: '#fff8e1',
        color: '#5d4037',
        fontStyle: 'italic',
    };

    return (
        <Accordion defaultExpanded disableGutters
            sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderTop: '1px solid #e0e0e0' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                        {group.groupLabel}
                    </Typography>
                    {hasPriorRow && (
                        <Typography variant="caption" color="text.secondary">
                            includes Prior Year {formatNum(prior)}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }} onClick={(e) => e.stopPropagation()}>
                    {transferSelectable && selectedIds.size > 0 && (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<UndoIcon />}
                                onClick={handleReturnSelected}
                                disabled={!onReturnSelected}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                Return ({selectedIds.size})
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<SwapHorizIcon />}
                                onClick={handleTransferSelected}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                Transfer Selected ({selectedIds.size})
                            </Button>
                        </>
                    )}
                    <Typography variant="body2" fontWeight={600}>
                        {formatNum(total)}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                {selectError && (
                    <Typography variant="caption" color="error" sx={{ px: 2, display: 'block' }}>
                        {selectError}
                    </Typography>
                )}
                <Box sx={{ overflow: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#fafafa' }}>
                                {transferSelectable && (
                                    <th style={{ ...cellSx, fontWeight: 600, width: 36 }}>
                                        <Checkbox
                                            size="small"
                                            indeterminate={someChecked && !allChecked}
                                            checked={allChecked}
                                            onChange={(_, c) => toggleAll(c)}
                                            disabled={selectableRows.length === 0}
                                        />
                                    </th>
                                )}
                                <th style={{ ...cellSx, fontWeight: 600, width: 30 }}>#</th>
                                <th style={{ ...cellSx, fontWeight: 600, width: 80 }}>Date</th>
                                <th style={{ ...cellSx, fontWeight: 600, width: 150 }}>Description</th>
                                <th style={{ ...cellSx, fontWeight: 600, width: 80 }}>Voucher</th>
                                <th style={{ ...cellSx, fontWeight: 600, minWidth: 280, width: '32%' }}>Details</th>
                                {displayGroup.columns.map((col) => (
                                    <th key={col.glCode} style={{ ...cellSx, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                                        {bankColumnShortLabel(col.nameEn, group.groupLabel.replace(/\s+(S\/A|C\/A|Cash)$/i, ''))}
                                    </th>
                                ))}
                                <th style={{ ...cellSx, fontWeight: 600, minWidth: 180 }}>Related Entries</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hasPriorRow && (
                                <tr>
                                    {transferSelectable && <td style={priorRowSx}></td>}
                                    <td style={priorRowSx}>—</td>
                                    <td style={priorRowSx}></td>
                                    <td style={{ ...priorRowSx, maxWidth: 200 }}>Prior Year b/f</td>
                                    <td style={priorRowSx}></td>
                                    <td style={priorRowSx}></td>
                                    {displayGroup.columns.map((col) => {
                                        const key = buildColumnKey(col);
                                        const val = excludedBankKeys.has(key)
                                            ? 0
                                            : resolvePriorYearAmount(key, summaryPriorYear);
                                        return (
                                            <td key={col.glCode} style={{ ...priorRowSx, textAlign: 'right' }}>
                                                {formatNum(val)}
                                            </td>
                                        );
                                    })}
                                    <td style={priorRowSx} />
                                </tr>
                            )}
                            {filteredRows.map((row, idx) => {
                                const pick = resolvePick(row);
                                const clickable = !!onRowClick && !!row.invoiceId;
                                const canSelect = transferSelectable && !!row.invoiceId;
                                const checked = canSelect && selectedIds.has(row.invoiceId);
                                return (
                                <tr
                                    key={row.invoiceId || idx}
                                    onClick={clickable ? () => onRowClick?.(row, pick.amount, pick.glCode) : undefined}
                                    style={{
                                        cursor: clickable ? 'pointer' : undefined,
                                        background: checked ? '#e3f2fd' : undefined,
                                    }}
                                    onMouseEnter={clickable ? (e) => {
                                        if (!checked) (e.currentTarget as HTMLTableRowElement).style.background = '#f5f5f5';
                                    } : undefined}
                                    onMouseLeave={clickable ? (e) => {
                                        (e.currentTarget as HTMLTableRowElement).style.background = checked ? '#e3f2fd' : '';
                                    } : undefined}
                                >
                                    {transferSelectable && (
                                        <td
                                            style={cellSx}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={!!checked}
                                                disabled={!canSelect}
                                                onChange={(_, c) => toggleRow(row, c)}
                                            />
                                        </td>
                                    )}
                                    <td style={cellSx}>{idx + 1}</td>
                                    <td style={cellSx}>{row.date || ''}</td>
                                    <td style={{ ...cellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.description || ''}>
                                        {row.description || ''}
                                    </td>
                                    <td style={cellSx}>{row.voucherNo || ''}</td>
                                    <td style={{ ...cellSx, maxWidth: 360, minWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.details || ''}>
                                        {row.details || ''}
                                    </td>
                                    {displayGroup.columns.map((col) => {
                                        const val = row.columnValues?.[buildColumnKey(col)];
                                        return (
                                            <td key={col.glCode} style={{ ...cellSx, textAlign: 'right' }}>
                                                {formatNum(val)}
                                            </td>
                                        );
                                    })}
                                    <td style={cellSx}>
                                        {buildBankRelatedEntries(row.columnValues, columnDefs).map((entry, j, arr) => (
                                            <span key={j} style={{ marginRight: j < arr.length - 1 ? 12 : 0 }}>
                                                <span style={{ fontWeight: 600 }}>{formatNum(entry.amount)}</span>
                                                {' '}
                                                <span style={{ color: '#666' }}>{entry.label}</span>
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

export default GlBankCashDetail;
